import { FC, Fragment, useCallback, useEffect, useRef, useState } from "react"
import { Loader2 } from "lucide-react"

import EngagementChatSystem from "./chat-system"
import EngagementChatUser from "./chat-user"
import EngagementChatInput from "./chat-input"
import { useFetchChatThreadQuery } from "../../../../../../redux/queries"
import {
  useInMobileView,
  useUserActivity,
  useVisibilityTracking
} from "../../../../../hooks"

interface EngagementChatsProps {
  leadId: string | undefined
}

const EngagementChats: FC<EngagementChatsProps> = ({ leadId }) => {
  const inMobileView = useInMobileView()
  const [contentHeight, setContentHeight] = useState<number>(0)
  const [isActive, setIsActive] = useState<boolean>(true)
  const [lastActiveTime, setLastActiveTime] = useState<number>(Date.now())
  const [showLoader, setShowLoader] = useState<boolean>(true)
  const scrollableRef = useRef<HTMLDivElement>(null)

  const { data: chatThread, isLoading } = useFetchChatThreadQuery(
    { lead_id: leadId! },
    {
      pollingInterval: isActive ? 5000 : 0,
      skip: !leadId || !isActive
    }
  )

  useVisibilityTracking(setIsActive)
  useUserActivity(lastActiveTime, setIsActive, setLastActiveTime)

  const calculateHeight = useCallback(() => {
    const headerHeight = 64
    const detailsHeight = inMobileView ? 278 : 211
    const chatSectionHeight = 156
    const viewportHeight = window.innerHeight
    const availableHeight =
      viewportHeight - headerHeight - detailsHeight - chatSectionHeight

    setContentHeight(availableHeight)
  }, [inMobileView])

  useEffect(() => {
    calculateHeight()
    window.addEventListener("resize", calculateHeight)
    return () => {
      window.removeEventListener("resize", calculateHeight)
    }
  }, [calculateHeight])

  useEffect(() => {
    if (scrollableRef.current) {
      scrollableRef.current.scrollTop = scrollableRef.current.scrollHeight
    }
  }, [showLoader])

  useEffect(() => {
    if (showLoader && !isLoading) {
      setShowLoader(false)
    }
  }, [isLoading, showLoader])

  if (showLoader) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2
          className="animate-spin h-24 w-24 text-primary-alt-400"
          aria-hidden="true"
        />
      </div>
    )
  }

  return (
    <div className="w-full h-full">
      <div className="flex flex-col">
        <div className="flex-1 flex flex-col bg-gray-100 rounded-lg p-4">
          <div
            ref={scrollableRef}
            className="relative flex-1 flex flex-col pt-2 pb-4 overflow-x-hidden overflow-y-auto"
            style={{ minHeight: contentHeight, maxHeight: contentHeight }}
          >
            {chatThread &&
              chatThread?.messages.map((message, index) => (
                <Fragment key={`chat-system-user-${index}`}>
                  {message.sender === "system" ? (
                    <EngagementChatSystem messageData={message} />
                  ) : (
                    <EngagementChatUser messageData={message} />
                  )}
                </Fragment>
              ))}

            {!chatThread?.messages.length && (
              <div className="absolute w-full text-center top-1/2 text-gray-400">
                No chats created
              </div>
            )}
          </div>
          <div className="mt-4">
            <EngagementChatInput />
          </div>
        </div>
      </div>
    </div>
  )
}

export default EngagementChats
